import { isNull, uniq } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch } from '../../../../hooks/react-redux';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { incompleteOpinionUpdateValue } from '../store';
import styles from './UploadOpinions.module.scss';
import { OpinionCommissionedBy, OpinionScope } from '../../../admin/opinions/store';

interface OpinionDetailsScopeProps {
    bespoke: number;
    scope: OpinionScope | null;
    additionalScopes: OpinionScope[] | undefined;
    sourceOpinionScopes: OpinionScope[];
}

export const OpinionDetailsScope: React.FC<OpinionDetailsScopeProps> = ({ bespoke, scope, additionalScopes, sourceOpinionScopes }) => {
    const dispatch = useAppDispatch();

    const setIndustryStandardOpinionScope = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(dropdownValue)) {
            value = (dropdownValue as DropdownOption).value;
        }
        dispatch(incompleteOpinionUpdateValue('scope', value));
        if (value === OpinionScope.GMRA_GMSLA_NETTING && bespoke === 0) {
            dispatch(incompleteOpinionUpdateValue('commissionedBy', OpinionCommissionedBy.ICMA));
        }
    }, [bespoke, dispatch]);

    const setBespokeOpinionScope = (value: DropdownOption | Options<DropdownOption> | null) => {
        let scopes: OpinionScope[] | null = null;
        if (!isNull(value)) {
            scopes = (value as Options<DropdownOption>).map(({ value }) => value as OpinionScope);
        }
        if (isNull(scopes) || scopes.length === 0) {
            dispatch(incompleteOpinionUpdateValue('scope', null));
            dispatch(incompleteOpinionUpdateValue('additionalScopes', []));
        } else {
            // first check if we have a scope
            if (isNull(scope)) {
                // then set the scope with the first option, any remainders become additional scopes
                scopes.map((scope, index) => {
                    if (index === 0) {
                        dispatch(incompleteOpinionUpdateValue('scope', scope));
                    } else {
                        const currentAdditionalScopes = additionalScopes || [];
                        const updatedAdditionalScopes = uniq([...currentAdditionalScopes, scope]);
                        dispatch(incompleteOpinionUpdateValue('additionalScopes', updatedAdditionalScopes));
                    }
                });
            } else {
                // we already have a scope so were just changing the additional scopes
                const newAdditionalScopes = scopes.filter(additionalScope => additionalScope !== scope);
                dispatch(incompleteOpinionUpdateValue('additionalScopes', newAdditionalScopes));
            }
        }
    };

    const opinionScopeOptions = useMemo(() => [
        { label: 'Validity and enforceability of NETTING arrangements (ISDA Master Agreement)', value: OpinionScope.ISDA_NETTING, disabled: sourceOpinionScopes.includes(OpinionScope.ISDA_NETTING) },
        { label: 'Validity and enforceability of COLLATERAL arrangements (Collateral Provider Insolvency)', value: OpinionScope.COLLATERAL_PROVIDER, disabled: sourceOpinionScopes.includes(OpinionScope.COLLATERAL_PROVIDER) },
        { label: 'Validity and enforceability of COLLATERAL arrangements (Collateral Taker Insolvency)', value: OpinionScope.COLLATERAL_TAKER, disabled: sourceOpinionScopes.includes(OpinionScope.COLLATERAL_TAKER) },
        { label: 'Validity and enforceability of NETTING arrangements (GMRA)', value: OpinionScope.GMRA_NETTING, disabled: sourceOpinionScopes.includes(OpinionScope.GMRA_NETTING) },
        { label: 'Validity and enforceability of NETTING arrangements (GMSLA)', value: OpinionScope.GMSLA_NETTING, disabled: sourceOpinionScopes.includes(OpinionScope.GMSLA_NETTING) },
        { label: 'Validity and enforceability of NETTING arrangements (GMRA & GMSLA)', value: OpinionScope.GMRA_GMSLA_NETTING, disabled: sourceOpinionScopes.includes(OpinionScope.GMRA_GMSLA_NETTING) },
        { label: 'Close-out, Set-off and Default Provisions - Client Reliance (ISDA/FIA Client Cleared OTC Derivatives Addendum)', value: OpinionScope.CLIENT_RELIANCE, disabled: sourceOpinionScopes.includes(OpinionScope.CLIENT_RELIANCE) }
    ], [sourceOpinionScopes]);

    const opinionScopeValue = useMemo(() => opinionScopeOptions.find(({ value }) => value === scope) || null, [opinionScopeOptions, scope]);

    const bespokeScopeValue = useMemo(() => {
        if (isNull(opinionScopeValue)) {
            return null;
        }
        let options = [opinionScopeValue];
        if (additionalScopes) {
            for (const additionalScope of additionalScopes) {
                const option = opinionScopeOptions.find(({ value }) => value === additionalScope)!;
                options.push(option);
            }
        }
        return options;
    }, [additionalScopes, opinionScopeOptions, opinionScopeValue]);

    return (
        <div data-testid='scope-wrapper' className={styles.inputWrapper}>
            <div className={styles.inputHeader}>Scope</div>
            {bespoke === 1 ? <Dropdown
                value={bespokeScopeValue}
                options={opinionScopeOptions}
                onChange={val => setBespokeOpinionScope(val)}
                testId='opinion-scope'
                menuPortalTarget={document.body}
                isMulti={true}
                isClearable={sourceOpinionScopes.length === 0}
            /> :
                <Dropdown
                    value={opinionScopeValue}
                    options={opinionScopeOptions}
                    onChange={setIndustryStandardOpinionScope}
                    testId='opinion-scope'
                    menuPortalTarget={document.body}
                    isClearable={sourceOpinionScopes.length === 0}
                />
            }
        </div>
    );
};
